/**
 * 게임 정의 린트 — 결정 #30(인라인 오류 검출). 컴파일러의 화이트리스트(결정 #17)는
 * "보안"을 지키지만, 제작자가 놓치기 쉬운 "조용한 오작동"은 막지 못한다. 이 린트는
 * 그런 실수를 만들기 전에 잡아 제작 경험을 개선한다(순수 함수, 결정 #10).
 *
 * 대표 사례(실제로 던전 템플릿에서 발생했던 버그): dice.dcKey가 mutableKeys에
 * 없으면 모델의 `@설정 dc = ...`가 화이트리스트에 걸러져 주사위 성공 판정이 조용히
 * 실패한다. 린트가 이를 경고로 잡는다.
 *
 * error: 게임이 컴파일/동작 불가(필수 필드 누락). warning: 동작하지만 의도와
 * 어긋날 가능성(존재하지 않는 키 참조 등).
 */
import type { GameDefinition } from "./game-definition.js";

export interface GameLintIssue {
  severity: "info" | "warning" | "error";
  /** Translation key for the message. */
  message: string;
  /** Translation arguments, if any. */
  args?: Record<string, string | number>;
  /** UI가 해당 섹션으로 안내할 수 있는 필드 힌트. */
  field?: string;
}

/**
 * 게임 정의를 검사해 문제 목록을 반환한다(결정 #30). 빈 배열이면 깨끗함. 호출부는
 * error가 하나라도 있으면 저장을 막거나 강하게 경고할 수 있다.
 */
export function lintGameDefinition(def: GameDefinition): GameLintIssue[] {
  const issues: GameLintIssue[] = [];

  // ---- 필수 필드(error) ----
  if (!def.title || !def.title.trim()) {
    issues.push({ severity: "error", message: "lint.game.titleEmpty", field: "title" });
  }
  if (!def.instructions || !def.instructions.trim()) {
    issues.push({
      severity: "warning",
      message: "lint.game.instructionsEmpty",
      field: "instructions",
    });
  }
  const stateKeys = def.initialState ? Object.keys(def.initialState) : [];
  const schemaKeys = (def.stateSchema ?? []).map((field) => field.key).filter(Boolean);
  if (stateKeys.length === 0) {
    issues.push({
      severity: "warning",
      message: "lint.game.noInitialState",
      field: "initialState",
    });
  }

  // 알려진 키 = 초기 상태 + 모델이 바꿀 수 있는 키(둘 다 정당한 참조 대상).
  // 알려진 키 = 초기 상태 + 모델 변경 허용 키 + 규칙이 산출하는 파생 키(then의 key).
  // 규칙은 모델이 못 건드리는 파생 상태(예: status)를 만들므로 정당한 참조 대상이다.
  // setup 필드 키(갭3)는 세션 시작 시 초기 상태에 병합되고, initRules는 파생 키를 만든다.
  const ruleKeys = (def.rules ?? []).flatMap((r) => (r.then ?? []).map((a) => a.key));
  const initRuleKeys = (def.initRules ?? []).flatMap((r) => (r.then ?? []).map((a) => a.key));
  const setupKeys = (def.setup ?? []).map((f) => f.key);
  const known = new Set<string>([
    ...stateKeys,
    ...schemaKeys,
    ...(def.mutableKeys ?? []),
    ...ruleKeys,
    ...initRuleKeys,
    ...setupKeys,
  ]);

  for (const key of duplicates(schemaKeys)) {
    issues.push({
      severity: "warning",
      message: "lint.game.stateSchemaDuplicateKey",
      args: { key },
      field: "stateSchema",
    });
  }
  for (const field of def.stateSchema ?? []) {
    if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
      issues.push({
        severity: "warning",
        message: "lint.game.stateSchemaInvalidBounds",
        args: { key: field.key },
        field: "stateSchema",
      });
    }
    if (
      typeof field.default === "number" &&
      ((field.min !== undefined && field.default < field.min) ||
        (field.max !== undefined && field.default > field.max))
    ) {
      issues.push({
        severity: "warning",
        message: "lint.game.stateSchemaDefaultOutOfBounds",
        args: { key: field.key },
        field: "stateSchema",
      });
    }
    if (field.derived && (field.mutable || def.mutableKeys?.includes(field.key))) {
      issues.push({
        severity: "warning",
        message: "lint.game.derivedStateMutable",
        args: { key: field.key },
        field: "stateSchema",
      });
    }
  }

  // ---- 주사위: dcKey가 mutableKeys에서 빠지면 판정이 조용히 실패(warning) ----
  if (def.dice?.dcKey && def.mutableKeys && !def.mutableKeys.includes(def.dice.dcKey)) {
    issues.push({
      severity: "warning",
      message: "lint.game.diceDcKeyMissing",
      args: { key: def.dice.dcKey },
      field: "dice",
    });
  }

  // ---- 종료 조건: 참조 키가 알려지지 않음(warning) ----
  for (const cond of def.endWhen ?? []) {
    if (cond.key && !known.has(cond.key)) {
      issues.push({
        severity: "warning",
        message: "lint.game.endWhenUnknownKey",
        args: { key: cond.key },
        field: "endWhen",
      });
    }
  }

  // ---- 클램프: 참조 키가 초기 상태에 없음(warning) ----
  for (const clamp of def.clamps ?? []) {
    if (clamp.min !== undefined && clamp.max !== undefined && clamp.min > clamp.max) {
      issues.push({
        severity: "warning",
        message: "lint.game.clampInvalidBounds",
        args: { key: clamp.key },
        field: "clamps",
      });
    }
    if (clamp.key && !known.has(clamp.key)) {
      issues.push({
        severity: "warning",
        message: "lint.game.clampUnknownKey",
        args: { key: clamp.key },
        field: "clamps",
      });
    }
  }

  // ---- 엔티티(갭1): 알려진 엔티티 필드 = template 필드 + roster의 모든 필드 키. ----
  const entityFieldKeys = new Set<string>([
    ...Object.keys(def.entityTemplate ?? {}),
    ...Object.values(def.entityRoster ?? {}).flatMap((fields) => Object.keys(fields)),
    "observed", // 구체화 시 자동 부여되는 필드.
  ]);
  const hasEntitySystem =
    !!def.entityTemplate ||
    !!def.entityRoster ||
    !!def.mutableEntityKeys ||
    !!def.entityClamps?.length;

  // mutableEntityKeys/entityClamps를 썼는데 엔티티 스키마(template/roster)가 없으면,
  // 구체화 기준이 없어 조용히 빈 엔티티만 생긴다(warning).
  if (hasEntitySystem && !def.entityTemplate && !def.entityRoster) {
    issues.push({
      severity: "warning",
      message: "lint.game.entityMissingSchema",
      field: "entityTemplate",
    });
  }

  // entityClamps가 엔티티 스키마에 없는 필드를 참조(warning).
  if (def.entityTemplate || def.entityRoster) {
    for (const clamp of def.entityClamps ?? []) {
      if (clamp.key && !entityFieldKeys.has(clamp.key)) {
        issues.push({
          severity: "warning",
          message: "lint.game.entityClampUnknownKey",
          args: { key: clamp.key },
          field: "entityClamps",
        });
      }
    }
    // mutableEntityKeys가 스키마에 없는 필드를 허용(warning — 오타 가능성).
    for (const k of def.mutableEntityKeys ?? []) {
      if (!entityFieldKeys.has(k)) {
        issues.push({
          severity: "warning",
          message: "lint.game.entityMutableUnknownKey",
          args: { key: k },
          field: "mutableEntityKeys",
        });
      }
    }
  }

  // ---- 조건부 규칙: when이 알 수 없는 키를 참조(warning). then은 파생 키를 새로
  //      만들 수 있으므로 검사하지 않는다(결정 #34). ----
  for (const rule of def.rules ?? []) {
    for (const cond of rule.when ?? []) {
      if (cond.key && !known.has(cond.key)) {
        issues.push({
          severity: "warning",
          message: "lint.game.ruleWhenUnknownKey",
          args: { key: cond.key },
          field: "rules",
        });
      }
    }
    if ((rule.then ?? []).length === 0) {
      issues.push({
        severity: "warning",
        message: "lint.game.ruleThenEmpty",
        field: "rules",
      });
    }
    for (const action of rule.then ?? []) {
      pushOutOfBoundsActionIssue(issues, action.key, action.value, def, "rules");
    }
  }
  if (hasRuleDependencyCycle(def.rules ?? [])) {
    issues.push({
      severity: "warning",
      message: "lint.game.rulePotentialCycle",
      field: "rules",
    });
  }

  // ---- setup 필드(갭3): 옵션이 비었거나(warning) default가 옵션에 없음(warning). ----
  for (const field of def.setup ?? []) {
    if (!field.options || field.options.length === 0) {
      issues.push({
        severity: "warning",
        message: "lint.game.setupOptionsEmpty",
        args: { key: field.key },
        field: "setup",
      });
      continue;
    }
    if (field.default !== undefined && !field.options.some((o) => o.value === field.default)) {
      issues.push({
        severity: "warning",
        message: "lint.game.setupDefaultInvalid",
        args: { key: field.key, default: field.default },
        field: "setup",
      });
    }
  }

  // ---- 초기 규칙(갭3): rules와 동일 검사(when 미지 키, 빈 then). 세션 시작 1회용. ----
  for (const rule of def.initRules ?? []) {
    for (const cond of rule.when ?? []) {
      if (cond.key && !known.has(cond.key)) {
        issues.push({
          severity: "warning",
          message: "lint.game.initRuleWhenUnknownKey",
          args: { key: cond.key },
          field: "initRules",
        });
      }
    }
    if ((rule.then ?? []).length === 0) {
      issues.push({
        severity: "warning",
        message: "lint.game.initRuleThenEmpty",
        field: "initRules",
      });
    }
    for (const action of rule.then ?? []) {
      pushOutOfBoundsActionIssue(issues, action.key, action.value, def, "initRules");
    }
  }

  // ---- 조건부 프롬프트 주입 게이트(갭2): when이 알 수 없는 키를 참조(warning),
  //      inject가 비었거나(warning) inject의 {{key}}가 알 수 없음(warning). ----
  for (const gate of def.gates ?? []) {
    for (const cond of gate.when ?? []) {
      if (cond.key && !known.has(cond.key)) {
        issues.push({
          severity: "warning",
          message: "lint.game.gateWhenUnknownKey",
          args: { key: cond.key },
          field: "gates",
        });
      }
    }
    if (!gate.inject || !gate.inject.trim()) {
      issues.push({
        severity: "warning",
        message: "lint.game.gateInjectEmpty",
        field: "gates",
      });
    } else {
      const seen = new Set<string>();
      for (const key of collectTemplateKeys(gate.inject)) {
        if (!seen.has(key) && !known.has(key)) {
          seen.add(key);
          issues.push({
            severity: "warning",
            message: "lint.game.gateInjectUnknownKey",
            args: { key },
            field: "gates",
          });
        }
      }
    }
  }

  // ---- viewSpec 바인딩: 참조 키가 알려지지 않음(warning) ----
  for (const entry of [
    { binding: def.hud, field: "hud" },
    { binding: def.view, field: "view" },
  ]) {
    const view = entry.binding;
    if (!view) continue;
    for (const bar of view.statBars ?? []) {
      if (bar.valueKey && !known.has(bar.valueKey)) {
        issues.push({
          severity: "warning",
          message: "lint.game.viewBarValueKeyUnknown",
          args: { label: bar.label || bar.valueKey, key: bar.valueKey },
          field: entry.field,
        });
      }
      if (bar.maxKey && !known.has(bar.maxKey)) {
        issues.push({
          severity: "warning",
          message: "lint.game.viewBarMaxKeyUnknown",
          args: { label: bar.label || bar.valueKey, key: bar.maxKey },
          field: entry.field,
        });
      }
    }
    for (const panel of view.statPanel ?? []) {
      if (panel.key && !known.has(panel.key)) {
        issues.push({
          severity: "warning",
          message: "lint.game.viewPanelKeyUnknown",
          args: { label: panel.label || panel.key, key: panel.key },
          field: entry.field,
        });
      }
    }
    if (view.inventoryKey && !known.has(view.inventoryKey)) {
      issues.push({
        severity: "warning",
        message: "lint.game.viewInventoryKeyUnknown",
        args: { key: view.inventoryKey },
        field: entry.field,
      });
    }
  }

  const assetKeys = (def.assets ?? []).map((asset) => asset.key).filter(Boolean);
  for (const key of duplicates(assetKeys)) {
    issues.push({
      severity: "warning",
      message: "lint.game.assetDuplicateKey",
      args: { key },
      field: "assets",
    });
  }
  const assetKeySet = new Set(assetKeys);
  const bindingIds = (def.assetBindings ?? []).map((binding) => binding.id).filter(Boolean);
  for (const id of duplicates(bindingIds)) {
    issues.push({
      severity: "warning",
      message: "lint.game.assetBindingDuplicateId",
      args: { id },
      field: "assetBindings",
    });
  }
  for (const binding of def.assetBindings ?? []) {
    if (!binding.id?.trim()) {
      issues.push({
        severity: "warning",
        message: "lint.game.assetBindingMissingId",
        field: "assetBindings",
      });
    }
    if (!binding.assetKey && !binding.assetId) {
      issues.push({
        severity: "warning",
        message: "lint.game.assetBindingMissingAsset",
        args: { id: binding.id || "(missing)" },
        field: "assetBindings",
      });
    }
    if (binding.assetKey && !assetKeySet.has(binding.assetKey)) {
      issues.push({
        severity: "warning",
        message: "lint.game.assetBindingUnknownAsset",
        args: { id: binding.id || binding.assetKey, key: binding.assetKey },
        field: "assetBindings",
      });
    }
    for (const cond of binding.when ?? []) {
      if (cond.key && !known.has(cond.key)) {
        issues.push({
          severity: "warning",
          message: "lint.game.assetBindingUnknownState",
          args: { id: binding.id || "(missing)", key: cond.key },
          field: "assetBindings",
        });
      }
    }
  }

  // ---- 상태 요약 템플릿: @(key) / {{key}} 자리표시자가 알려지지 않음(warning) ----
  if (def.stateSummaryTemplate) {
    const seen = new Set<string>();
    for (const key of collectTemplateKeys(def.stateSummaryTemplate)) {
      if (!seen.has(key) && !known.has(key)) {
        seen.add(key);
        issues.push({
          severity: "warning",
          message: "lint.game.stateSummaryTemplateUnknownKey",
          args: { key },
          field: "stateSummaryTemplate",
        });
      }
    }
  }

  return issues;
}

function collectTemplateKeys(template: string): string[] {
  const keys: string[] = [];
  const legacy = /\{\{\s*([\w.-]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = legacy.exec(template)) !== null) keys.push(m[1]!);

  const roqeet = /@\(\s*([\p{L}_][\p{L}\p{N}_.-]*)\s*\)/gu;
  while ((m = roqeet.exec(template)) !== null) keys.push(m[1]!);
  return keys;
}

function duplicates(values: string[]): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) dupes.add(value);
    seen.add(value);
  }
  return [...dupes];
}

function pushOutOfBoundsActionIssue(
  issues: GameLintIssue[],
  key: string,
  value: number | string | boolean,
  def: GameDefinition,
  field: string,
): void {
  if (typeof value !== "number") return;
  const bounds = stateBoundsFor(key, def);
  if (!bounds) return;
  if (
    (bounds.min !== undefined && value < bounds.min) ||
    (bounds.max !== undefined && value > bounds.max)
  ) {
    issues.push({
      severity: "warning",
      message: "lint.game.ruleActionOutOfBounds",
      args: { key, value },
      field,
    });
  }
}

function stateBoundsFor(
  key: string,
  def: GameDefinition,
): { min?: number; max?: number } | undefined {
  const schema = def.stateSchema?.find((field) => field.key === key);
  const out: { min?: number; max?: number } = {
    ...(schema?.min !== undefined ? { min: schema.min } : {}),
    ...(schema?.max !== undefined ? { max: schema.max } : {}),
  };
  for (const clamp of def.clamps ?? []) {
    if (clamp.key !== key) continue;
    if (clamp.min !== undefined) out.min = clamp.min;
    if (clamp.max !== undefined) out.max = clamp.max;
  }
  return out.min === undefined && out.max === undefined ? undefined : out;
}

function hasRuleDependencyCycle(rules: NonNullable<GameDefinition["rules"]>): boolean {
  const edges = new Map<string, Set<string>>();
  for (const rule of rules) {
    const inputs = (rule.when ?? []).map((cond) => cond.key).filter(Boolean);
    const outputs = (rule.then ?? []).map((action) => action.key).filter(Boolean);
    for (const input of inputs) {
      const set = edges.get(input) ?? new Set<string>();
      for (const output of outputs) set.add(output);
      edges.set(input, set);
    }
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (key: string): boolean => {
    if (visiting.has(key)) return true;
    if (visited.has(key)) return false;
    visiting.add(key);
    for (const next of edges.get(key) ?? []) {
      if (visit(next)) return true;
    }
    visiting.delete(key);
    visited.add(key);
    return false;
  };

  for (const key of edges.keys()) {
    if (visit(key)) return true;
  }
  return false;
}
